import type { ReactNode } from "react";

import { ThemeToggle } from "@/components/ThemeToggle";

/**
 * Shell de las pantallas de autenticación: pantalla dividida (split).
 * Izquierda: panel de marca (verde Consultek + logo centrado). Deliberadamente
 * SIN claim ni métricas comerciales: esta pantalla la ve el cliente y esas
 * afirmaciones no necesariamente calzan con el servicio que se le presta. Se
 * oculta bajo `lg`. Derecha: contenido del formulario, centrado, con toggle de
 * tema y pie de página. El logo aparece arriba del formulario sólo en mobile.
 */
export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="bg-background min-h-screen w-full lg:grid lg:grid-cols-[1.05fr_1fr]">
      {/* ---------- Panel de marca (izquierda) ---------- */}
      <aside className="text-primary-foreground relative hidden overflow-hidden bg-gradient-to-br from-primary to-[#2f7a2e] p-12 lg:flex lg:items-center lg:justify-center">
        {/* Decoración: glow suave + curvas de nivel (evoca el dominio geoespacial) */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-white/10 blur-3xl"
        />
        <svg
          aria-hidden
          className="pointer-events-none absolute -bottom-16 -left-16 h-[28rem] w-[28rem] text-white/10"
          viewBox="0 0 400 400"
          fill="none"
        >
          {[40, 80, 120, 160, 200, 240].map((r) => (
            <ellipse
              key={r}
              cx="200"
              cy="220"
              rx={r}
              ry={r * 0.7}
              stroke="currentColor"
              strokeWidth="1.5"
            />
          ))}
        </svg>

        {/* Marca */}
        <div className="relative flex items-center gap-4">
          <img
            src="/consultek-mark.png"
            alt="Consultek"
            className="h-14 w-14 object-contain brightness-0 invert"
          />
          <span className="text-display">Consultek</span>
        </div>
      </aside>

      {/* ---------- Panel del formulario (derecha) ---------- */}
      <main className="relative flex min-h-screen flex-col items-center justify-center px-6 py-12">
        <div className="absolute right-5 top-5">
          <ThemeToggle />
        </div>

        <div className="w-full max-w-sm">
          {/* Logo visible sólo en mobile (el panel de marca está oculto) */}
          <div className="mb-10 flex items-center justify-center gap-2.5 lg:hidden">
            <img
              src="/consultek-mark.png"
              alt="Consultek"
              className="h-8 w-8 object-contain"
            />
            <span className="text-xl font-extrabold tracking-tight">
              Consul<span className="text-primary">tek</span>
            </span>
          </div>

          {children}

          <p className="text-muted-foreground mt-10 text-center text-xs">
            © {new Date().getFullYear()} Consultek SpA. Todos los derechos reservados.
          </p>
        </div>
      </main>
    </div>
  );
}
